import React from "react";
import type { PlanWallet } from "../api/supPlanApi";
import { chatState, questionsLeft } from "../model/planGate";
import { fmtDate, planCopy } from "../model/planCopy";
import { planSalesHere } from "../model/salesHere";
import { PlanIcon } from "./PlanIcon";
import s from "./supPlan.module.scss";

/**
 * «Вопросов специалисту: 32 · до 29.08.2027 · Продлить» (доска
 * SpecialistHeader); срок прошёл — «0 · срок закончился 12.01.2027»
 * (ExpiredChat). Вопросов не было вовсе — только число и «Продлить».
 */
export const SpecialistPlanBar: React.FC<{
    wallet: PlanWallet;
    lang: string;
    onRenew: () => void;
}> = ({ wallet, lang, onRenew }) => {
    const c = planCopy(lang);
    const st = chatState(wallet);
    const left = questionsLeft(wallet);
    const date = fmtDate(wallet.questions?.until, lang);
    return (
        <section aria-label={c.chatLabel} className={s.specBalance} data-testid="plan-spec-bar">
            <div className={s.specText}>
                <div className={s.specRow}>
                    <span>{c.chatLabel}</span>
                    <span className={s.specNum} data-testid="plan-spec-left">
                        {left}
                    </span>
                </div>
                {st !== "none" && date && (
                    <div className={`${s.specDate} ${st === "ended" ? s.specDateEnded : ""}`}>
                        {st === "ended" ? c.ended(date) : c.until(date)}
                    </div>
                )}
            </div>
            {planSalesHere() && (
                <button type="button" className={s.pill} aria-label={c.renewAria} onClick={onRenew}>
                    {c.renew}
                </button>
            )}
        </section>
    );
};

/**
 * «Новый вопрос — после продления» (доска ExpiredChat) — на месте формы нового вопроса.
 * С `onRenew` рядом «Продлить» — окно пакетов: строка не тупик.
 */
export const NewQuestionLater: React.FC<{ lang: string; onRenew?: () => void }> = ({ lang, onRenew }) => {
    const c = planCopy(lang);
    return (
        <div className={s.later} data-testid="plan-new-later">
            <p className={s.laterText}>{c.newAfterRenew}</p>
            {onRenew && planSalesHere() && (
                <button
                    type="button"
                    className={s.pill}
                    aria-label={c.renewAria}
                    onClick={onRenew}
                    data-testid="plan-new-later-renew"
                >
                    {c.renew}
                </button>
            )}
        </div>
    );
};

/** Строка об оплате после возврата со Stripe. */
export const PlanToast: React.FC<{ text: string }> = ({ text }) => (
    <div className={s.toast} role="status" aria-live="polite" data-testid="plan-paid">
        <PlanIcon name="check" size={18} />
        <span>{text}</span>
    </div>
);

/**
 * Вид пакетов не прочитан (сбой у человека на пакетах или сервер не знает режима): одна
 * спокойная строка «Не удалось загрузить» и «Повторить» — не прежние токены и не их окно.
 */
export const PlanLoadFailed: React.FC<{ lang: string; onRetry: () => void }> = ({ lang, onRetry }) => {
    const c = planCopy(lang);
    return (
        <div role="status" className={`${s.banner} ${s.ended}`} data-testid="plan-load-failed">
            <p className={s.bannerText}>{c.loadFailed}</p>
            <button type="button" className={s.pill} onClick={onRetry}>
                {c.retry}
            </button>
        </div>
    );
};
